import { useEffect, useRef, useState } from 'react';
import HeroField from './HeroField';

interface LetterDef { ch: string; ix: number; iy: number; delay: number }

// Compositional anchors (centre points as fraction of stage), not random.
const LETTERS: LetterDef[] = [
  { ch: 'A', ix: 0.2, iy: 0.2, delay: 0 },
  { ch: 'V', ix: 0.79, iy: 0.16, delay: 0.07 },
  { ch: 'E', ix: 0.1, iy: 0.58, delay: 0.03 },
  { ch: 'N', ix: 0.9, iy: 0.55, delay: 0.1 },
  { ch: 'O', ix: 0.31, iy: 0.92, delay: 0.05 },
  { ch: 'R', ix: 0.74, iy: 0.9, delay: 0.13 },
];

const clamp = (v: number, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2);

// Progress phases (gate-owned progress 0..1, not scroll position):
// 0 → ASSEMBLE_END letters travel into the word; ASSEMBLE_END → 1 descriptor fades in.
const ASSEMBLE_END = 0.8;
const OUTRO_END = 1;
const MAX_DELAY = 0.13;
// Wheel distance (px) that maps to a full assembly.
const GATE_DISTANCE = 1500;
// Completed word is held; a NEW gesture (after this idle gap) is required to release.
// Brief hold on the completed word; after it, ongoing momentum scrolls the page.
const HOLD_MS = 200;

type GateState = 'IDLE' | 'ASSEMBLING' | 'COMPLETE' | 'RELEASED';

export default function HeroArchive() {
  const sectionRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const letterRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const ghostRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const layersRef = useRef<HTMLDivElement>(null);
  const introRef = useRef<HTMLDivElement>(null);
  const outroRef = useRef<HTMLDivElement>(null);
  const cueRef = useRef<HTMLAnchorElement>(null);
  const [ready, setReady] = useState(false);
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    const stage = stageRef.current;
    if (!section || !stage) return undefined;

    const mqMobile = window.matchMedia('(max-width: 767px), (pointer: coarse)');
    const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const isCompact = () => mqMobile.matches || mqReduce.matches;
    setCompact(isCompact());

    let w = 0;
    let h = 0;
    let finals: { x: number; y: number }[] = [];
    let target = 0; // gate-owned progress
    let shown = 0; // rendered progress (eases toward target)
    let state: GateState = 'IDLE';
    let completeAt = 0;
    let raf = 0;
    let started = false;

    const measure = () => {
      const r = stage.getBoundingClientRect();
      w = r.width;
      h = r.height;
      finals = ghostRefs.current.map((el) => {
        if (!el) return { x: w / 2, y: h / 2 };
        const b = el.getBoundingClientRect();
        return { x: b.left - r.left + b.width / 2, y: b.top - r.top + b.height / 2 };
      });
    };

    const render = (p: number) => {
      const anim = clamp(p / ASSEMBLE_END);
      LETTERS.forEach((l, i) => {
        const el = letterRefs.current[i];
        const f = finals[i];
        if (!el || !f) return;
        const t = easeInOut(clamp((anim - l.delay) / (1 - MAX_DELAY)));
        const x = lerp(l.ix * w, f.x, t);
        const y = lerp(l.iy * h, f.y, t);
        const s = lerp(1.1, 1, t);
        el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) translate(-50%, -50%) scale(${s.toFixed(3)})`;
      });
      // idle float amplitude: fades out as soon as assembly begins
      stage.style.setProperty('--idle', (1 - clamp(anim / 0.12)).toFixed(3));
      const soft = easeInOut(anim);
      if (layersRef.current) {
        layersRef.current.style.opacity = String(lerp(1, 0.22, soft));
        layersRef.current.style.transform = `translate3d(0, ${(-16 * soft).toFixed(1)}px, 0)`;
      }
      if (introRef.current) {
        const o = 1 - clamp(anim / 0.32);
        introRef.current.style.opacity = String(o);
        introRef.current.style.transform = `translate3d(0, ${(-12 * (1 - o)).toFixed(1)}px, 0)`;
        introRef.current.style.visibility = o < 0.01 ? 'hidden' : 'visible';
      }
      if (outroRef.current) {
        const o = easeInOut(clamp((p - ASSEMBLE_END) / (OUTRO_END - ASSEMBLE_END)));
        outroRef.current.style.opacity = String(o);
        outroRef.current.style.transform = `translate3d(0, ${(14 * (1 - o)).toFixed(1)}px, 0)`;
      }
      if (cueRef.current) cueRef.current.style.opacity = String(1 - clamp(p / 0.06));
    };

    // ---------- render loop (runs only while shown != target) ----------
    const loop = () => {
      raf = 0;
      const diff = target - shown;
      shown = Math.abs(diff) < 0.0015 ? target : shown + diff * 0.2;
      render(shown);
      if (shown >= 1 && state === 'ASSEMBLING') {
        // final frame has been rendered → COMPLETE
        state = 'COMPLETE';
        completeAt = performance.now();
      }
      if (shown < 1 && state === 'COMPLETE') state = 'ASSEMBLING';
      if (shown !== target) raf = requestAnimationFrame(loop);
    };
    const kick = () => { if (!raf && started) raf = requestAnimationFrame(loop); };

    const setTarget = (v: number) => {
      target = clamp(v);
      if (target < 1 && state !== 'ASSEMBLING') state = target > 0 ? 'ASSEMBLING' : 'IDLE';
      if (target > 0 && state === 'IDLE') state = 'ASSEMBLING';
      kick();
    };

    const forceResolved = () => { target = 1; shown = 1; state = 'RELEASED'; render(1); };

    const gateActive = () => started && !isCompact() && window.scrollY <= 1;

    // ---------- wheel gate (only acts while the hero sits at the top) ----------
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      const now = performance.now();
      if (!gateActive()) return;

      let dy = e.deltaY;
      if (e.deltaMode === 1) dy *= 16;
      else if (e.deltaMode === 2) dy *= window.innerHeight;
      dy = clamp(dy, -140, 140); // a single huge delta can never jump the sequence

      if (dy > 0) {
        if (target < 1 || state === 'ASSEMBLING') {
          // ASSEMBLING: consume input, page frozen. Never complete AND release on one event.
          e.preventDefault();
          setTarget(target + dy / GATE_DISTANCE);
          return;
        }
        if (state === 'COMPLETE') {
          const held = now - completeAt >= HOLD_MS;
          if (held) { state = 'RELEASED'; return; } // let native scroll proceed
          e.preventDefault(); // brief hold on the completed word
          return;
        }
        // RELEASED → native scroll
        return;
      }

      if (dy < 0 && target > 0) {
        // reverse assembly while at the top
        e.preventDefault();
        setTarget(target + dy / GATE_DISTANCE);
      }
    };

    // ---------- keyboard parity ----------
    const onKey = (e: KeyboardEvent) => {
      if (!gateActive()) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const down = ['ArrowDown', 'PageDown', ' '].includes(e.key) && !e.shiftKey;
      const up = e.key === 'ArrowUp' || e.key === 'PageUp' || (e.key === ' ' && e.shiftKey);
      const step = e.key === 'ArrowDown' || e.key === 'ArrowUp' ? 0.1 : 0.3;
      if (down && (target < 1 || state === 'ASSEMBLING')) { e.preventDefault(); setTarget(target + step); return; }
      if (down && state === 'COMPLETE') {
        if (performance.now() - completeAt < HOLD_MS) { e.preventDefault(); return; }
        state = 'RELEASED';
        return;
      }
      if (up && target > 0) { e.preventDefault(); setTarget(target - step); }
    };

    // Page moved by other means (scrollbar drag, anchor link, restored position):
    // show the resolved brand rather than leaving a half-built word off-screen.
    const onScroll = () => {
      if (isCompact() || !started) return;
      if (window.scrollY > 1 && shown < 1) forceResolved();
      else if (window.scrollY > 1 && state !== 'RELEASED') state = 'RELEASED';
      else if (window.scrollY <= 1 && state === 'RELEASED') { state = 'COMPLETE'; completeAt = 0; }
    };

    const onResize = () => {
      setCompact(isCompact());
      measure();
      if (isCompact()) forceResolved();
      else render(shown);
    };

    const start = () => {
      if (!stageRef.current) return;
      started = true;
      measure();
      // Mobile / touch: show the assembled word directly (no gate, no timed settle).
      if (mqReduce.matches || mqMobile.matches || window.scrollY > 1) {
        forceResolved();
      } else {
        render(0);
      }
      setReady(true);
    };

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(start);
    } else {
      start();
    }
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    mqMobile.addEventListener('change', onResize);
    mqReduce.addEventListener('change', onResize);

    return () => {
      started = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      mqMobile.removeEventListener('change', onResize);
      mqReduce.removeEventListener('change', onResize);
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative h-[100svh]" aria-label="AVENOR — Accounting and Advisory" data-compact={compact ? 'true' : 'false'}>
      <div ref={stageRef} className="hero-stage paper-texture relative h-[100svh] w-full overflow-hidden bg-background-50">
        {/* archival image layers */}
        <div ref={layersRef} className="pointer-events-none absolute inset-0 will-change-transform" aria-hidden="true">
          <HeroField />
        </div>

        {/* invisible final word for measurement */}
        <div aria-hidden="true" className="invisible absolute left-1/2 top-[44%] flex -translate-x-1/2 -translate-y-1/2 gap-[0.03em]">
          {LETTERS.map((l, i) => (
            <span key={l.ch} ref={(el) => { ghostRefs.current[i] = el; }} className="hero-letter block">{l.ch}</span>
          ))}
        </div>

        {/* the actual moving letters */}
        {LETTERS.map((l, i) => (
          <span
            key={l.ch}
            ref={(el) => { letterRefs.current[i] = el; }}
            aria-hidden="true"
            className="hero-letter absolute left-0 top-0 z-10 block select-none text-primary-500 mix-blend-multiply will-change-transform"
            style={{ opacity: ready ? 0.94 : 0, transition: 'opacity .6s ease' }}
          >
            <span
              className="hero-float block"
              style={{ animationDuration: `${7 + (i % 3) * 1.6}s`, animationDelay: `${-i * 1.3}s`, animationDirection: i % 2 ? 'reverse' : 'normal' }}
            >
              {l.ch}
            </span>
          </span>
        ))}

        {/* opening editorial text */}
        <div ref={introRef} className="absolute inset-x-0 top-1/2 z-20 mx-auto w-full max-w-[560px] -translate-y-1/2 px-8 text-center">
          <p className="font-label text-[11px] uppercase tracking-[0.2em] text-foreground-950">— Independent practice —</p>
          <h1 className="mt-5 font-poster text-[30px] font-semibold uppercase leading-[1.05] tracking-[0.04em] text-foreground-950 md:text-[40px]">
            A considered approach to every number.
          </h1>
          <p className="mx-auto mt-5 max-w-[440px] text-sm leading-relaxed text-foreground-700 md:text-[15px]">
            Independent accounting, tax and advisory expertise for businesses, individuals and the decisions that shape their future.
          </p>
        </div>

        {/* resolved brand composition */}
        <div ref={outroRef} className="hero-descriptor absolute inset-x-0 z-20 px-6 text-center opacity-0">
          <p className="font-label text-xs uppercase tracking-[0.34em] text-foreground-950">Accounting &amp; Advisory</p>
          <p className="mx-auto mt-4 max-w-[420px] text-sm leading-relaxed text-foreground-700 md:text-[15px]">
            A considered approach to every number — for businesses, individuals and the decisions that shape their future.
          </p>
        </div>

        <a
          ref={cueRef}
          href="#the-firm"
          className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 cursor-pointer flex-col items-center gap-2 font-label text-[10px] uppercase tracking-[0.2em] text-foreground-700"
        >
          <span>Scroll</span>
          <span className="flex h-9 w-9 items-center justify-center rounded-full border border-primary-500 text-primary-500"><i className="ri-arrow-down-line" /></span>
        </a>
      </div>
    </section>
  );
}